import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";
import { initSafeAreaInsets } from "./lib/safeArea";

// Iter51 — Push native safe-area insets into CSS vars at startup.
// Capacitor 7 Android edge-to-edge often returns 0 for
// env(safe-area-inset-bottom); this reads the real values from the
// capacitor-plugin-safe-area native plugin and exposes them as
// --safe-top / --safe-right / --safe-bottom / --safe-left.
initSafeAreaInsets();
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Journey from "./pages/Journey";
import Play from "./pages/Play";
import Rewards from "./pages/Rewards";
import Challenges from "./pages/Challenges";
import Stats from "./pages/Stats";
import Themes from "./pages/Themes";
import Store from "./pages/Store";
import Leaderboard from "./pages/Leaderboard";
import Endurance from "./pages/Endurance";
import DailyChallenge from "./pages/DailyChallenge";
import WeeklyTournament from "./pages/WeeklyTournament";
import PaymentStatus from "./pages/PaymentStatus";
import { PrivacyPolicy, TermsOfService } from "./pages/Legal";
import Downloads from "./pages/Downloads";
import DeleteAccount from "./pages/DeleteAccount";
import Debug from "./pages/Debug";
import Profile from "./pages/Profile";
import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] text-[#2C332A]/60">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PlayRoute() {
  // key by level so navigating to the next board fully remounts Play
  // (its startedRef guard otherwise blocks re-init on param change).
  const { worldId, levelId } = useParams();
  return <Play key={`${worldId}/${levelId}`} />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/downloads" element={<Downloads />} />
      <Route
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/journey" element={<Journey />} />
        <Route path="/challenges" element={<Challenges />} />
        <Route path="/stats" element={<Stats />} />
        <Route path="/themes" element={<Themes />} />
        <Route path="/store" element={<Store />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/endurance" element={<Endurance />} />
        <Route path="/daily-challenge" element={<DailyChallenge />} />
        <Route path="/weekly-tournament" element={<WeeklyTournament />} />
        <Route path="/payments/status" element={<PaymentStatus />} />
        <Route path="/debug" element={<Debug />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/rewards" element={<Rewards />} />
      </Route>
      <Route path="/play/:worldId/:levelId" element={<Protected><PlayRoute /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster richColors position="top-center" />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
